import type { DataTableFilterField } from '@pesto/ui/components/DataTable/DataTable.types';
import type { ColumnDef, TableState } from '@tanstack/react-table';

import { Button, Typography } from '@pesto/ui';
import { DataTableColumnHeader } from '@pesto/ui/components/DataTable/DataTableColumnHeader';
import { BooleanTag } from '@pesto/ui/features/SmartTable/components/BooleanTag';
import {
  generateSelectableFilterOptions,
  setColumnFilterAndSortKey,
} from '@pesto/ui/features/SmartTable/utils/helpers';
import { formatDate } from '@pesto/utils';

import type { CreditApplication } from '../../../__generated__/graphql/api';

import { CollateralType, CreditApplicationStatus } from '../../../__generated__/graphql/api';

const columnIdNames = {
  creditAppId: setColumnFilterAndSortKey('idSearch', 'idSortDirection'),
  insertedAt: setColumnFilterAndSortKey('', 'insertedAtSortDirection'),
  status: setColumnFilterAndSortKey('statusSelections', 'statusSortDirection'),
  collateralType: setColumnFilterAndSortKey('collateralTypeSelections', 'collateralTypeSortDirection'),
  paymentPreference: setColumnFilterAndSortKey('paymentPreferenceSelections', 'paymentPreferenceSortDirection'),
  userId: setColumnFilterAndSortKey('pestoUserIdSearch', 'pestoUserIdSortDirection'),
  firstName: setColumnFilterAndSortKey('userFirstNameSearch', 'userFirstNameSortDirection'),
  lastName: setColumnFilterAndSortKey('userLastNameSearch', 'userLastNameSortDirection'),
};

export const useCreditApplicationsConfig = () => {
  const listCreditApplicationsFilterFields: DataTableFilterField<any>[] = [
    {
      label: 'Credit Application ID',
      value: columnIdNames.creditAppId,
      placeholder: 'Search by Credit Application ID',
    },
    {
      label: 'Status',
      value: columnIdNames.status,
      options: Object.entries(CreditApplicationStatus).map(([, value]) => ({
        label: value[0]?.toUpperCase() + value.slice(1),
        value: value,
        withCount: true,
      })),
    },
    {
      label: 'Collateral Type',
      value: columnIdNames.collateralType,
      options: Object.entries(CollateralType).map(([, value]) => ({
        label: value[0]?.toUpperCase() + value.slice(1),
        value: value,
        withCount: true,
      })),
    },

    {
      label: 'First Name',
      value: columnIdNames.firstName,
      placeholder: 'First Name',
    },
    {
      label: 'Last Name',
      value: columnIdNames.lastName,
      placeholder: 'Last Name',
    },
    {
      label: 'Custom Filters',
      value: 'customFilters',
      options: generateSelectableFilterOptions({
        'Display first Credit Applications only': 'firstOf',
      }),
      isCustomCheckboxFilter: true,
    },
  ];

  const getListCreditApplicationsColumns = (
    handleRowClicked: (data: CreditApplication) => void,
  ): ColumnDef<CreditApplication>[] => [
    {
      id: columnIdNames.creditAppId,
      header: 'Credit Application ID',
      accessorFn: (row) => row.id,
      cell: ({ row }) => row.original.id,
    },
    {
      id: columnIdNames.insertedAt,
      accessorFn: (row) => row.insertedAt,
      header: ({ column }) => <DataTableColumnHeader column={column} title="Created" />,
      cell: ({ row }) => (
        <Typography variant={'body'} className={'w-auto text-nowrap'}>
          {formatDate(row.original.insertedAt)}
        </Typography>
      ),
    },
    {
      id: columnIdNames.status,
      header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
      accessorFn: (row) => row.status,
      cell: ({ row }) => row.original.status,
    },

    {
      id: columnIdNames.userId,
      header: 'User ID',
      accessorFn: (row) => row.pestoUser?.id,
      cell: ({ row }) => row.original.pestoUser?.id,
    },
    {
      id: columnIdNames.firstName,
      header: 'First Name',
      accessorFn: (row) => row.pestoUser?.firstName,
      cell: ({ row }) => row.original.pestoUser?.firstName,
    },
    {
      id: columnIdNames.lastName,
      header: 'Last Name',
      accessorFn: (row) => row.pestoUser?.lastName,
      cell: ({ row }) => row.original.pestoUser?.lastName,
    },
    {
      id: 'billingCity',
      header: 'Billing City',
      accessorFn: (row) => row.pestoUser?.billingAddress?.city,
      cell: ({ row }) => row.original.pestoUser?.billingAddress?.city,
    },
    {
      id: 'billingState',
      header: 'Billing State',
      accessorFn: (row) => row.pestoUser?.billingAddress?.state,
      cell: ({ row }) => row.original.pestoUser?.billingAddress?.state,
    },
    {
      id: 'billingEmail',
      header: 'Billing Email',
      accessorFn: (row) => row.pestoUser?.email,
      cell: ({ row }) => row.original.pestoUser?.email,
    },
    {
      id: 'friendsAndFamily',
      header: 'Friends and Family',
      accessorFn: (row) => row.pestoUser?.isFriendsAndFam,
      cell: ({ row }) => BooleanTag({ value: row.original.pestoUser?.isFriendsAndFam ?? false }),
    },
    {
      id: 'view',
      header: 'Action',
      enablePinning: true,
      cell: (row) => (
        <Button onClick={() => handleRowClicked(row.cell.row.original)} variant={'outline'}>
          View
        </Button>
      ),
    },
  ];

  const initialTableState: Partial<TableState> = {
    columnPinning: { left: ['view'] },
    sorting: [{ id: 'insertedAtSortDirection', desc: true }],
    columnVisibility: {
      userId: false,
      friendsAndFamily: false,
      hasUserShipment: false,
      needsOutboundShippingKitCreated: false,
      needsOutboundShippingKitSent: false,
      outboundShippingKitSent: false,
    },
  };

  return { listCreditApplicationsFilterFields, getListCreditApplicationsColumns, initialTableState };
};
